import { Page, PageHeader } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';

export const ReleaseNotes = () => (
  <Page>
    <PageHeader title="Release Notes" />
    <div className="container">
      <Stack divider="line" gap="lg">
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.4.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de pagina Inzage MPR (Marktpartijenregister) tonen filters
                  nu alleen nog waarden waarvoor daadwerkelijk registraties
                  bestaan in de actuele dataset. Hierdoor selecteren gebruikers
                  geen filters meer die tot lege overzichten leiden en kunnen
                  relevante gegevens sneller worden gevonden.
                </li>
                <li>
                  Er is ondersteuning toegevoegd in de Mijn PIE
                  Backend-for-Frontend (BFF) voor een nieuwe webrol ten behoeve
                  van de LV-LV functionaliteit. Hierdoor kan deze rol op
                  dezelfde uniforme wijze als de overige webrollen binnen PIE
                  worden uitgeleverd en beheerd richting de frontend.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Een probleem verholpen waarbij gebruikers tijdens het
                  uitloggen onterecht eerst op een error pagina terechtkwamen.
                  Het uitlogproces verloopt nu weer stabiel.
                </li>
                <li>
                  Een fout opgelost waarbij het wisselen van pagina's ervoor
                  zorgde dat gebruikers meer dan het toegestane maximum van 50
                  tickets konden selecteren voor bulk-afsluiting. De limiet van
                  maximaal 50 gelijktijdig af te sluiten tickets wordt nu
                  consistent over meerdere pagina's heen bewaakt.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De comment-sectie in de berichtenbox is herontworpen naar een
                  overzichtelijkere gebruikersinterface.
                </li>
                <li>
                  De behandelaarslijst (op zowel de ticketoverzichtspagina als
                  de detailpagina) toont nu uitsluitend gebruikers met de webrol
                  "ticket", waardoor dubbele vermeldingen verdwijnen en
                  verkeerde toewijzingen worden voorkomen.
                </li>
                <li>
                  Terugkerende bezoekers van www.partnersinenergie.nl hoeven
                  cookievoorkeuren voortaan slechts eenmalig te accepteren in
                  plaats van bij elk bezoek. Deze cookies zijn nu voor een jaar
                  geldig.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Een probleem opgelost op pie publiek waarbij het uitklapmenu
                  "Marktrol" in het registratieformulier voor buitenlandse
                  partijen verspringt bij openen of alleen werkt bij ver
                  uitzoomen.
                </li>
                <li>
                  Een probleem opgelost waarbij invoervelden in gebruiksbeheer
                  niet correct als gewijzigd (dirty) werden gemarkeerd bij
                  aanpassingen.
                </li>
                <li>
                  Een probleem opgelost waarbij na het wisselen van marktrol een
                  foutmelding (403) werd getoond in plaats van naar de
                  hoofdpagina te redirecten.
                </li>
                <li>
                  Een probleem opgelost waarbij een gewijzigde behandelaar niet
                  correct werd weergegeven na navigatie tussen de
                  ticketdetailpagina en het ticketoverzicht. In sommige gevallen
                  werd nog de vorige behandelaar getoond in plaats van de
                  actuele behandelaar.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Netbeheerders en leveranciers kunnen nu het
                  marktpartijregister inzien. Het register biedt een overzicht
                  van marktpartijen en toegang tot de gegevens van de
                  betreffende marktpartij.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Netbeheerders met de marktrol RNB, LNB, GDS of LV kunnen via
                  Partners in Energie een overzicht inzien van alle actieve,
                  geregistreerde marktpartijen. Het overzicht bevat onder meer
                  de onderneming, marktpartij, subunits, marktrol, EAN13,
                  peildatum en status. Daarnaast is uitgebreide
                  marktpartijregistratiedata beschikbaar op een detailpagina.
                </li>
                <li>
                  Voor de toegang tot marktpartijregistratiedata zijn nieuwe
                  dienstrollen toegevoegd. Hiermee wordt de toegang beperkt tot
                  de juiste marktrollen en kunnen interne EDSN-medewerkers alle
                  MPR-data inzien.
                </li>
                <li>
                  De streefdatum van de netbeheerder is nu zichtbaar op de
                  ticketdetailpagina. Leveranciers zien hierdoor direct wanneer
                  uiterlijk een reactie van de netbeheerder wordt verwacht.
                </li>
                <li>
                  In het V2-registratieformulier kan nu een DVT-beheerder worden
                  opgegeven. De voornaam, achternaam en het e-mailadres worden
                  gekoppeld aan Dynamics en getoond in de
                  registratiebevestiging.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Mijn PiE gebruikt nu het pagination-component uit
                  pie-components. Dit zorgt voor een consistentere
                  gebruikerservaring en minder dubbele code.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Pie publiek is gemoderniseerd door de overstap naar Tailwind
                  CSS 4, waarbij de bestaande vormgeving behouden is gebleven.
                </li>
                <li>
                  De gebruikersinterface van Mijn PiE is consistenter gemaakt.
                </li>
                <li>
                  Bij het in bulk afsluiten van tickets kunnen maximaal vijftig
                  tickets tegelijk worden verwerkt. Dit voorkomt foutmeldingen
                  bij grote aantallen tickets.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Een probleem opgelost waarbij maatwerkpagina's binnen pie
                  publiek niet werden vertaald wanneer een gebruiker naar de
                  Engelse weergave overschakelde.
                </li>
                <li>
                  Problemen met de vormgeving en zichtbaarheid van onder meer de
                  notificatiebanners in Mijn PiE zijn opgelost.
                </li>
                <li>
                  Een regressie opgelost waardoor de V2-registratiefrontend niet
                  meer functioneerde.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De kwartaalcontrole van Craft CMS en pie-public is uitgevoerd.
                  Hierbij zijn Docker-builds, opstartprocedures, databaseherstel
                  en GraphQL-generatie gecontroleerd. Ook zijn plugins
                  bijgewerkt en eventuele breaking changes opgelost.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Factuurcontacten worden nu geraadpleegd vanuit de
                  MPR-CPR-cache.
                </li>
                <li>
                  Er is een extra rol toegevoegd voor het inzien van alle
                  marktpartijgegevens, ter voorbereiding op toekomstige
                  functionaliteit.
                </li>
                <li>
                  Het ontwikkel-, deployment- en releaseproces is vernieuwd. De
                  nieuwe werkwijze zorgt voor snellere en betrouwbare releases.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Een probleem opgelost waarbij een voorletter niet altijd werd
                  ingevuld bij het aanmaken van een contactpersoon vanuit PiE.
                </li>
                <li>
                  Een probleem opgelost waardoor niet kon worden doorgeklikt
                  naar een contactgroep vanaf de detailpagina van een
                  factuurcontact.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.3.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De gegevens voor het inzien van het factuurcontactregister
                  worden nu opgehaald uit de MPR-cache in plaats van Dynamics.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.2.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het maken van een nieuwe ticket is het bij bepaalde
                  subcategorieën nu verplicht om een bijlage toe te voegen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Enkele UI aanpassingen om de leesbaarheid van de website te
                  verbeteren.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Fix doorgevoerd waarbij kpi cards op de home pagina weer
                  correct laden.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.2.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Beheerpagina voor contactpersonen uitgebreid met een
                  detailpagina waarop contactgegevens en contactgroepen
                  aangepast kunnen worden.
                </li>
                <li>
                  Het is nu mogelijk om dezelfde contactpersoon binnen één
                  contactgroep te koppelen aan meerdere subunits.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Het maatwerkdataformulier is heringericht zodat deze beter
                  aansluit op de vragen rondom clusterverzoeken.
                </li>
                <li>
                  De voorbeeldbestanden voor het aanleveren van documenten staan
                  nu op de beginpagina, zodat deze direct beschikbaar zijn voor
                  het indienen van een aanvraag.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.2.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het maken van een nieuwe ticket is het bij bepaalde
                  subcategorieën nu verplicht om een bijlage toe te voegen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Enkele UI aanpassingen om de leesbaarheid van de website te
                  verbeteren.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Fix doorgevoerd waarbij kpi cards op de home pagina weer
                  correct laden.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.2.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Het interesseformulier welke op de V2 versie van de website
                  staat, is nu beschikbaar op de publieke website via
                  partnersinenergie.nl/interesseformulier.
                </li>
                <li>
                  Bij het registeren van een nieuwe marktpartij zijn nu zeven
                  nieuwe verplichte contactgroepen toegevoegd om de
                  contactpersoon hiervoor uit te vragen. Deze contactgroepen
                  zijn ook verplicht geworden in mijn.pie voor beheerders van
                  het CPR.
                </li>
                <li>
                  Bij het registreren van een nieuwe marktpartij wordt nu de
                  voornaam uitgevraagd i.p.v. initalen. Ook is het tussenvoegsel
                  verwijderd en wordt dit samengevoegd met de achternaam.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de CPR beheerpagina bij de contactpersonen tab is de naam
                  kolom iets breder gemaakt.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de CPR beheerpagina is bij de contactpersonen tab een bug
                  opgelost waarbij niet meer de gehele naam van de
                  contactpersoon getoond werd wanneer je hier met je muis over
                  zweefde.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Voorbereiding voor implementatie van een tours systeem dat
                  gebruikers stap voor stap wegwijs maakt op partners in
                  energie.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op het registratieformulier kunnen nu voor de contactgroepen
                  Leveringszekerheid, Meetcorrectie GV en Verrekenproces
                  Meetfouten GV ook andere contactpersonen worden geregistreerd.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het creeëren van een nieuwe ticket is extra validatie
                  toegevoegd, zodat gebruiker een foutmelding krijgt wanneer in
                  de titel van het ticket speciale tekens gebruikt worden.
                </li>
                <li>
                  Enkele UI aanpassingen om de leesbaarheid van de website te
                  verbeteren.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de home pagina staan nu voor gebruikers met de berichtenbox
                  rol Kpi cards, waarmee in één oogopslag informatie over de
                  werkvoorraad van de berichtenbox te zien is.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De ticket weergave dropdown op de berichtenbox pagina is
                  verplaatst naar boven.
                </li>
                <li>
                  De ticket weergave "Open tickets verreken Telemetrie" is
                  weggehaald.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Loophole opgelost waarbij beheerders via contactpersonenbeheer
                  alsnog de laatste contactpersoon uit een verplichte
                  contactgroep konden verwijderen. Bij verplichte groepen
                  verschijnt nu een waarschuwing en kan de contactpersoon niet
                  worden verwijderd. Bij niet-verplichte groepen moet de
                  beheerder de verwijdering bevestigen.
                </li>
                <li>
                  Registratietype 'subunit' is nu weer zichtbaar op
                  factuurcontactregister.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op Pie publiek staan de congestierapportages weer gesorteerd
                  op publicatiedatum
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij de CPR kwaliteitscontrole check wordt nu ook gecontroleerd
                  of verplichte contactgroepen minstens één gekoppelde
                  contactpersoon bevatten. Wanneer dit niet het geval is, kan de
                  data niet gevalideerd worden en wordt er getoond welke
                  contactgroepen nog een contactpersoon mist.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Enkele verbeteringen doorgevoerd aan het voorlopige
                  registratieformulier op Pie publiek.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 26.1.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op Pie publiek is nieuw een formulier beschikbaar gemaakt
                  waarbij buitenlandse partijen een voorlopige registratie
                  kunnen indienen.
                </li>
                <li>
                  Bij het registreren of wijzigen van een marktpartij wordt voor
                  de administratieve marktpartij (subunit) van een leverancier
                  een nieuw verplicht veld uitgevraagd: de verschijningsvorm.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De paginatieknoppen zijn vereenvoudigd, wat heeft geleid tot
                  het oplossen van diverse gerelateerde bugs.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.4.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Vanwege een belangrijke upgrade van het systeem kunnen de
                  filters op de nieuwspagina niet langer meer gebruikt worden.
                  Deze zijn daarom verwijderd. Het blijft mogelijk om op datum
                  te sorteren.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.4.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De oude mijn.partnersinenergie gaat uit! De bèta omgeving is
                  nu de standaard omgeving voor alle gebruikers. Alle Bèta tags
                  zijn verwijderd.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.4.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de support pagina is een extra tegel toegevoegd "Zo werkt
                  Partners in Energie".
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Het e-mailadres op de support pagina voor vragen over
                  dataverzoeken is veranderd.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het indienen van een dataverzoek werden niet alle waardes
                  van stap 1 correct ingevoerd wanneer vanaf stap 2 of stap 3
                  teruggenavigeerd werd. Dit is nu opgelost.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.4.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het veld eventdatum bij subcategorie KV Disputen is het nu
                  niet meer mogelijk om een datum in de toekomst in te vullen.
                  Er is hiervoor nu validatie toegevoegd.
                </li>
                <li>
                  Als je een ticket aanmaakt vanuit het
                  aansluitingsinformatiescherm wordt nu het EAN nummer
                  overgenomen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Afbeeldingen van nieuwsitems op de home pagina hebben nu
                  allemaal weer hetzelfde formaat.
                </li>
                <li>
                  Menu overlapt niet meer over de pagina wanneer de pagina is
                  geopend op kleinere resoluties.
                </li>
                <li>
                  Tickets met status "Gesloten" kunnen niet meer heropend
                  worden.
                </li>
                <li>
                  Links op de datadiensten pagina werken weer en het voorbeeld
                  toestemminsgformulier kan weer gedownload worden.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Het bijlage veld wordt niet meer onterecht verplicht wanneer
                  je meerdere opmerkingen achter elkaar plaatst zonder bijlage.
                  Ook geven lege comments nu consistente foutmeldingen.
                </li>
                <li>
                  Nieuwsberichten laden nu correct na het wisselen van taal in
                  de applicatie. Alle artikelen worden netjes getoond in plaats
                  van slechts één.
                </li>
                <li>
                  In het dataformulier kun je nu enkel specifieke jaartallen
                  selecteren, of de optie "Anders" selecteren, maar niet beide
                  tegelijk. Ook is het mogelijk om bijlages toe te voegen aan
                  het dataformulier.
                </li>
                <li>
                  De ticketdetailpagina werkt nu correct met browservertaling
                  wanneer je browser is ingesteld op een andere taal dan
                  Nederlands.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De kennisbankartikel overzichtspagina heeft een nieuw
                  uiterlijk gekregen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Enkele tekstuele wijzigingen op de ideeënpagina en de support
                  pagina.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het invullen van een dataverzoek wordt nu in de pdf de
                  energiesoort assesdata/verbruiksdata weer correct gevuld.
                </li>
                <li>
                  Tabellen bij kennisbankartikelen worden nu correct
                  weergegeven.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij de contactgroepen onder beheer, is een nu
                  kwaliteitscontrole toegevoegd. Wanneer de gegevens voor het
                  laatst meer dan 3 maanden geleden geverifieerd zijn, wordt er
                  gevraagd deze opnieuw te verifiëren.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het indienen van een dataverzoek is het nu mogelijk om aan
                  het einde van stap 1 nog extra bijlages toe te voegen.
                </li>
                <li>
                  Op de contactpersonenregisterschermen zijn de benamingen van
                  de koppen 'Organisatie' en 'Administratieve marktpartij'
                  gewijzigd naar respectievelijk 'Marktpartij' en 'Subunit'.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bug fixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de ticketdetailpagina is dikgedrukte tekst in een reactie
                  vanuit de netbeheerder weer zichtbaar.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het creëren van een ticket of een non-conformiteit ticket,
                  wordt bij bepaalde categorieën gevraagd of er klantimpact is.
                  Bij deze vraag kan voor Ja of Nee gekozen worden.
                </li>
                <li>
                  Bij het zoeken in kennisbankartikelen, worden nu ook artikelen
                  getoond waarbij de zoekterm overeenkomt met de inhoud van het
                  artikel.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij tickets waarvan de oorsprong Netbeheerder is, wordt geen
                  tevredenheidsscore meer uitgevraagd en kunnen tickets direct
                  afgesloten worden.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het heropenen van een ticket kan je nu direct een bijlage
                  toevoegen bij de beschrijving, zodat dit niet meer achteraf
                  gedaan hoeft te worden.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.3.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Tickets waarbij de netbeheerder een interne actie heeft
                  uitgezet om de vraag te beantwoorden, kunnen niet meer
                  geannuleerd worden. In dit geval komt er een melding bij het
                  ticket hierover te staan.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de ticket pagina kan er bij het filter Oorsprong niet meer
                  gekozen worden voor Onbekend, aangezien dit geen functie had.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bij het kopiëren van de inhoud van een ticket, komt er geen
                  HTML tekst meer mee.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.2.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Er is een taalswitch toegevoegd aan de website, waarmee je de
                  taal van de website kunt wijzigen. De opties zijn Nederlands
                  en Engels.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.2.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De ideeënpagina is als laatste onderdeel vernieuwd en nu
                  beschikbaar in de bètaomgeving. Op deze pagina vind je
                  informatie over hoe en waar je jouw ideeën en
                  verbetervoorstellen kunt indienen. Met deze toevoeging zijn
                  alle bèta-labels verwijderd. De oude versie van de website
                  blijft voorlopig nog toegankelijk.
                </li>
                <li>
                  Er is een support pagina toegevoegd aan de website, waarop
                  duidelijk wordt aangegeven met welke vragen je bij welk
                  contactpunt terechtkunt.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De header van de website is bijgewerkt en bevat nu een directe
                  link naar de nieuwe supportpagina. Daarnaast heeft het
                  accountmenu een vernieuwd uiterlijk gekregen voor een
                  verbeterde gebruikerservaring.
                </li>
                <li>
                  Het dataverzoekformulier is aangepast: de selectie van de
                  gewenste periode is nu dynamisch opgezet. Hierdoor worden
                  automatisch de laatste vijf kalenderjaren vanaf de huidige
                  datum weergegeven.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Titels van gerelateerde kennisbankartikelen worden bij
                  lengteoverschrijding nu automatisch op de volgende regel
                  geplaatst en blijven daarmee binnen de contentbox.
                </li>
                <li>
                  Op de ticketdetailpagina wordt schuingedrukte tekst in de
                  ticketomschrijving en reacties nu wel weergegeven.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.2.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de overzichtspagina van factuurcontacten worden nu ook
                  suborganisaties weergegeven.
                </li>
                <li>
                  Op de overzichtspagina van factuurcontacten kan nu worden
                  gezocht en gesorteerd op naam, EAN en registratietype.
                </li>
                <li>
                  De detailpagina van een factuurcontact heeft een nieuw ontwerp
                  gekregen. Ook worden nu het KvK-nummer en de EAN van de
                  subregistratie getoond.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op het dataverzoekformulier zijn de jaren dynamisch gemaakt
                  bij de vraag voor over welke periode je de data wilt
                  ontvangen.
                </li>
                <li>
                  De "volgende" knop op het dataverzoekformulier wordt niet meer
                  onjuist weergegeven.
                </li>
                <li>
                  Op het marktpartijregistratie-overzicht worden nu alleen de
                  preferente partijen getoond die door de organisatie zelf zijn
                  opgegeven, niet langer ook de partijen die de organisatie van
                  de ingelogde gebruiker hebben opgegeven als preferente partij.
                  Dit is nog wel in te zien op het "Preferente Partijen
                  Administratie" tabblad.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.2.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Er is een versimpeld dataverzoeken formulier toegevoegd voor
                  ACM, CBS en NEA.
                </li>
                <li>
                  Als organisatiebeheerder kan je nu ook jouw preferentie
                  partijen inzien en inzien welke partij jou heeft opgegeven als
                  preferentie partij.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.1.6
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Voor de marktrollen regionale netbeheerder, landelijke
                  netbeheerder, leverancier en GDS-eigenaren is het
                  factuurcontactregister toegevoegd.
                </li>
                <li>
                  Als organisatiebeheerder kan je nu contactpersonen die je zelf
                  hebt toegevoegd ook verwijderen via beheer/contactpersonen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Als organisatiebeheerder kan je nu contactpersonen die je zelf
                  hebt toegevoegd aanpassen via beheer/contactpersonen.
                </li>
                <li>
                  Het laatst bijgewerkt veld van een ticket wordt weer
                  bijgewerkt.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.1.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Als organisatiebeheerder kan je nu de gegevens van je
                  marktpartij ingezien welke geregisteerd staan in het
                  marktpartijregister.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 25.1.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Extra uitleg toegevoegd bij het invullen van de
                  tevredenheidsmeting, over wat je moet doen wanneer je feedback
                  hebt over het platform.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Opsomminglijst is nu weer zichtbaar bij nieuwsartikelen op
                  bèta.
                </li>
                <li>Telefoonnummer is nu ook zichtbaar bij ean storingen</li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 24.4.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Het dataverzoekformulier is nu ook beschikbaar. </li>
                <li>
                  De aansluitingsinformatie is nu ook beschikbaar als losse
                  pagina.
                </li>
                <li>
                  Het is nu mogelijk om bij een ticket / opmerking een
                  screenshot of andere bestanden te plakken (ctrl + v), waardoor
                  deze meteen worden toegevoegd als bijlage.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Tijdens het inloggen kan je meteen een account selecteren en
                  deze komt nu ook overeen met het actieve account in PiE.
                </li>
                <li>
                  Op de account pagina is onder het wachtwoord nu een link
                  toegevoegd welke verwijst naar de DVT pagina voor als je
                  wijzigingen aan je account wilt doen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Het filter voor marktrol LNB is nu ook zichtbaar.</li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 24.4.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Op de pagina voor organisatiebeheer is een nieuw tabblad
                  toegevoegd waar notificatie-instellingen beheerd kunnen
                  worden.
                </li>
                <li>
                  Het is nu mogelijk om screenshots of andere bestanden te
                  plakken in een commentaar veld waardoor ze als bijlage worden
                  toegevoegd.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Contactpersonen- en contactgroepenbeheer zijn verplaatst onder
                  Organisatiebeheer.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Ticketnummer regel is nu weer klikbaar gemaakt om naar de
                  detailpagina van het ticket te gaan.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.4.0
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Organisatiebeheer is toegevoegd aan het menu en wordt de
                  centrale plek voor alle organisatiegerelateerde
                  beheermogelijkheden. Hier kunnen gebruikers worden aangemaakt,
                  hun details worden bekeken en aangepast, en teams kunnen
                  worden toegevoegd en beheerd.
                </li>
                <li>
                  In teamsbeheer, onderdeel van organisatiebeheer, kunnen teams
                  nu worden aangemaakt, beheerd, en kunnen leden aan teams
                  worden toegevoegd of verwijderd.
                </li>
                <li>
                  Je profielgegevens zijn nu beschikbaar en te vinden onder het
                  accountmenu rechtsboven in het scherm.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De tickets sluit bulkactie is verduidelijkt aan de hand van
                  een nieuwe tekst.
                </li>
                <li>
                  Het is nu mogelijk om meer gebruikers in de contacten-dropdown
                  te laden.
                </li>
                <li>
                  Hoofd- en subcategorieën worden nu ook gefilterd op marktrol
                  en aansluitingstype (groot/kleinverbruik).
                </li>
                <li>
                  De marktrol onder de accountinformatie is weggehaald, omdat
                  deze nu achter de naam van de marktpartij wordt getoond.
                </li>
                <li>
                  Het is niet meer mogelijk om ongeldige karakters te gebruiken
                  in omschrijvingsvelden.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bug opgelost waarbij de titel van een ticket niet getoond werd
                  in de tekst wanneer je deze heropend.
                </li>
                <li>
                  Bug opgelost waarbij er een lege rij werd getoond wanneer er
                  geen CPR-contact gekoppeld was aan een groep.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.3.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Er is een nieuwe bulkactie toegevoegd om meerdere tickets op
                  gelezen of ongelezen te zetten.
                </li>
                <li>
                  Er is een nieuwe pagina op partnersinenergie.nl toegevoegd, om
                  gemakkelijk te navigeren naar de Bèta of de oude omgeving van
                  Mijn Partners in Energie
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De tickets sluiten bulkactie is verduidelijkt aan de hand van
                  een nieuwe tekst.
                </li>
                <li>Tabelkolommen hebben nu consistentere breedtes</li>
                <li>
                  Het is nu mogelijk om meer gebruikers in de contacten dropdown
                  te laden bij het toevoegen van een contactpersoon in het cpr.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.3.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  CPR beheerders kunnen nu nieuwe contactpersonen aanmaken.
                </li>
                <li>CPR beheerders kunnen contactpersonen nu bijwerken.</li>
                <li>
                  CPR beheerders kunnen nu bij het toevoegen van een
                  contactpersoon aan de contactgroep, zoeken op een
                  contactpersoon.
                </li>
                <li>
                  Lange onderwerpen bij tickets worden nu afgekapt en hier kan
                  overheen gehovert worden om de volledige titel te bekijken.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Ontwerp van contactpersonenregister verbeterd en tabjes
                  hernoemd.
                </li>
                <li>
                  Er is een venster gekomen wanneer op feedback geven geklikt
                  wordt, welke verwijst naar een e-mailadres of de service desk.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.3.0
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Het contactpersonenregister is nu beschikbaar samen met het
                  contactpersonen beheer.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Problemen met de chat widget zijn verholpen.</li>
                <li>Het downloaden van bijlages werkt weer.</li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.2.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Validatie bij het aanmaken van een non-conformiteit ticket is
                  verbeterd.
                </li>
                <li>
                  Probleem verholpen waarbij het sorteren niet altijd werkte.
                </li>
                <li>
                  Links worden nu ook weergegevens als deze door de netbeheerder
                  opgevoerd worden bij een reactie op een ticket.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.2.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De optie "Geen" is nu ook aan de team select toegevoegd, zodat
                  er ook op tickets gefilterd kan worden zonder team.
                </li>
                <li>
                  In de aansluitingsinformatie is nu altijd de EAN te zien die
                  vanuit CTS wordt meegestuurd.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Behandelaarlijst op ticket bevat nu het tussenvoegsel van de
                  naam en de lijst staat gesoteerd op alfabetische volgorde.
                </li>
                <li>
                  Bij het sorteren worden kolommen nu altijd eerst aflopend
                  gesorteerd.
                </li>
                <li>
                  Tabel dat door RNB in een reactie op een ticket wordt
                  toegevoegd, is nu ook zichtbaar.
                </li>
                <li>
                  Vertalingen van het oorsprong filter op de ticketpagina zijn
                  toegevoegd, waardoor hier de juiste tekst komt te staan.
                </li>
                <li>
                  Probleem opgelost waarbij reacties op tickets die via bèta
                  werden aangemaakt, niet zichtbaar waren op het oude portaal.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.2.1
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Probleem verholpen waarbij de chatwidget niet altijd zichtbaar
                  werd en waar de netbeheerder soms niet kon zien met wie ze aan
                  het chatten waren.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.2.0
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Gesprek omtrent de feedback op tickets over de afhandeling
                  hiervan (voorheen CES), is nu ook beschikbaar op bèta!
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Ticket teller loopt nu tot 999+ en heeft een andere kleur
                  gekregen om deze duidelijker te maken.
                </li>
                <li>
                  EAN-codes zijn nu zichtbaar tijdens het aanmaken van een
                  ticket.
                </li>
                <li>Na het uitloggen land je nu op de publieke website.</li>
                <li>
                  Wanneer er een foutmelding wordt weergegeven, heb je nu de
                  mogelijkheid om naar de homepage te navigeren.
                </li>
                <li>
                  Als je sessie verlopen is, kom je nu op een pagina waar je
                  opnieuw kunt inloggen.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bug opgelost waarbij netbeheerder niet automatisch werd
                  ingevuld bij het invullen van een gas EAN, terwijl deze
                  informatie wel vanuit CTS kwam.
                </li>
                <li>
                  De chatwidget wordt nu alleen getoond als deze geladen is en
                  gekoppeld is aan een gebruikersaccount, welke de bug waarbij
                  netbeheerders niet zagen met wie zij aan het chatten waren,
                  zou moeten oplossen.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>

        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.1.0
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe Features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Gemakkelijk zoeken in kennisbankartikelen met de nieuwe
                  zoekbalk.
                </li>
                <li>
                  Gerelateerde kennisbankartikelen verschijnen nu bij het
                  aanmaken van tickets, op de ticketdetailpagina, de
                  kennisbankdetailpagina en bij nieuwsitems.
                </li>
                <li>Kennisbankartikelen zijn nu gemakkelijker te lezen.</li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De nieuwsdetailpagina heeft een nieuw uiterlijk gekregen.
                </li>
                <li>
                  Het onderwerp invoerveld wordt nu automatisch ingevuld bij een
                  Non-Conformiteit ticket.
                </li>
                <li>
                  Zoekbalk toegevoegd aan de filters op de ticketpagina wanneer
                  er meer dan 15 resultaten zijn.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Sorteren op categorie werkt nu correct in de kennisbank.
                </li>
                <li>
                  Probleem verholpen waarbij de behandelaar en de
                  gelezen/ongelezen status niet meteen werden bijgewerkt in de
                  ticketlijsten.
                </li>
                <li>
                  Bug opgelost waarbij je naar een foutpagina werd gestuurd
                  wanneer je op de terug-knop op de ticketpagina klikte.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.0.5
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Zoeken op nieuws artikelen is toegevoegd.</li>
                <li>
                  Ticket teller toegevoegd om te tonen hoeveel ongelezen tickets
                  er open staan.
                </li>
                <li>
                  Non-conformiteitstickets kunnen nu aangemaakt worden in de
                  nieuw ticket wizard.
                </li>
                <li>
                  Kennisbankartikelen zijn nu beschikbaar op de kennisbank
                  pagina en kunnen op categorie worden gefilterd.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  We houden nu bij welke acties vaak worden gebruikt door middel
                  van Google Analytics.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Bug opgelost waarbij tijdens het aanmaken van een ticket EAN
                  verplicht was nadat deze verkeerd was ingevuld, terwijl het
                  andere veld wel al was ingevuld.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.0.4
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Nieuws overzicht pagina filtering en sortering.</li>
                <li>
                  Inhoudsopgave voor nieuwsdetail bericht toegevoegd en style
                  aanpassingen gedaan om overeen te komen met design.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Na het aanmaken van een ticket kom je niet meer terecht op een
                  leeg scherm.
                </li>
                <li>
                  Bug waarbij gebruiker niet getoond werd in de chat opgelost.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.0.3
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Notificatie banner toegevoegd, zodat gebruiker geïnformeerd
                  kan worden wanneer er informatie of problemen gemeld moeten
                  worden op de website.
                </li>
                <li>
                  Onze nieuwe home pagina met een eenvoudige manier om door de
                  site te navigeren.
                </li>
                <li>Ook het laatste nieuws is te zien op de home pagina.</li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>Styling van checkboxes is verbeterd.</li>
                <li>
                  Ticket filters worden nu beter vast gehouden als je door de
                  site heen navigeert.
                </li>
                <li>
                  Update van de bulk acties 'behandelaar toewijzen' en 'team
                  toewijzen', om het overeen te laten komen met het design van
                  de website.
                </li>
                <li>
                  Zoekbalk in de header is verwijderd aangezien deze geen
                  functie had.
                </li>
                <li>
                  Bij afgehandeld, afgesloten en geannuleerde tickets kan er
                  geen comment meer worden geplaatst.
                </li>
                <li>
                  EAN codes die bij tickets horen zijn nu altijd zichtbaar, ook
                  als een gebruiker geen rechten heeft tot
                  aansluitngsinformatie.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Forceer een login als de gebruiker met een eHerkenning token
                  automatisch probeert in te loggen.
                </li>
              </ul>
            </Text>
          </Stack>
        </Stack>
        <Stack className="gap-8">
          <Text as="h1" variant="h2">
            Versie 0.0.2
          </Text>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Nieuwe features
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  EAN-nummers kunnen nu eenvoudig worden gekopieerd vanuit het
                  ticketdetailscherm.
                </li>
                <li>
                  Bulkactie toegevoegd aan het ticketoverzicht voor het
                  toewijzen aan een behandelaar.
                </li>
                <li>
                  Bulkactie toegevoegd aan het ticketoverzicht voor het
                  toewijzen aan een team.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Verbeteringen
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  De overgang tussen het inlogscherm en 'Mijn Partners in
                  Energie' is verfijnd.
                </li>
                <li>
                  Pagina's zijn nu voorzien van paginatitels voor een betere
                  navigatie.
                </li>
                <li>
                  Google Analytics is geïntegreerd om inzicht te krijgen in het
                  gebruik van de website.
                </li>
                <li>
                  Datums worden nu op een consistente wijze weergegeven op het
                  ticketdetailscherm.
                </li>
                <li>De website is nu vertaalbaar voor een breder publiek.</li>
                <li>Formulieren worden nu op een verbeterde manier opgezet.</li>
                <li>
                  Diverse verbeteringen in de algehele gebruikerservaring.
                </li>
              </ul>
            </Text>
          </Stack>
          <Stack gap="sm">
            <Text as="h2" variant="h4">
              Bugfixes
            </Text>
            <Text as="div">
              <ul className="list-disc space-y-1">
                <li>
                  Gebruikers in Dynamics kunnen nu zien met wie er wordt gechat
                  in de bètaversie.
                </li>
                <li>Het CES-formulier werkt nu naar behoren.</li>
              </ul>
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </div>
  </Page>
);
